import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Button, Pagination } from 'react-bootstrap';
import { FaCalendar, FaArrowRight, FaTag, FaArrowLeft, FaClock } from 'react-icons/fa';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLanguage } from './LanguageProvider';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Get_All_Blogs_Action } from '../Redux/Actions/BlogsAction';
import SEO from './SEO';

gsap.registerPlugin(ScrollTrigger);

export const MainBlogsCards = () => {
  const { currentLang, isInitialized } = useLanguage();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const blogsPerPage = 6;
  const sectionRef = useRef(null);

  const langKey = (currentLang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isAR = langKey === 'ar';

  const resblogs = useSelector((state) => state.AllBlogs.Blog);
  const blogsLoading = useSelector((state) => state.AllBlogs.loading);

  const getImageUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    let cleanUrl = url.replace(/^undefined\//, '');
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) return cleanUrl;
    if (cleanUrl.startsWith('/')) return `http://localhost:8000${cleanUrl}`;
    return `http://localhost:8000/${cleanUrl}`;
  };

  const stripHtml = (value = "") => {
    if (!value || typeof value !== "string") return "";
    return value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  };

  const truncateText = (text, maxLength = 110) => {
    const cleanText = stripHtml(text);
    if (!cleanText) return "";
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  const translations = {
    en: {
      pageTitle: 'Editorial Insights',
      pageSubtitle: 'Expert perspectives on Spain residency, European mobility, and strategic relocation.',
      breadcrumbHome: 'Home',
      breadcrumbCurrent: 'Insights',
      readMore: 'Read Full Insight',
      loading: 'Loading Insights...',
      noBlogs: 'No insights available at the moment.',
      previous: 'Previous',
      next: 'Next'
    },
    ar: {
      pageTitle: 'مقالات ورؤى تحريرية',
      pageSubtitle: 'وجهات نظر الخبراء حول الإقامة في إسبانيا، والتنقل في أوروبا، والتخطيط الاستراتيجي للانتقال.',
      breadcrumbHome: 'الرئيسية',
      breadcrumbCurrent: 'المقالات',
      readMore: 'اقرأ المقال كاملاً',
      loading: 'جاري تحميل المقالات...',
      noBlogs: 'لا توجد مقالات متوفرة حالياً.',
      previous: 'السابق',
      next: 'التالي'
    }
  };

  const t = useCallback((key) => translations[langKey]?.[key] || translations.en[key] || key, [langKey]);

  useEffect(() => {
    dispatch(Get_All_Blogs_Action(`page=${currentPage}&limit=${blogsPerPage}`));
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (resblogs) {
      setTotalPages(resblogs?.pagination?.totalPages || 1);
      setTotalItems(resblogs?.pagination?.totalItems || 0);
    }
  }, [resblogs]);

  useEffect(() => {
    if (!isInitialized) return;
    const ctx = gsap.context(() => {
      // Banner Reveal
      gsap.from('.blog-banner-reveal', {
        y: 50, opacity: 0, duration: 1.2, stagger: 0.2, ease: 'power4.out'
      });

      // Cards Grid Reveal
      const cards = gsap.utils.toArray('.editorial-card-wrap');
      if (cards.length > 0) {
        gsap.from(cards, {
          y: 60, opacity: 0, duration: 1, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.blog-editorial-grid', start: 'top 85%' }
        });
      }

      // Parallax Banner
      gsap.to('.blog-page-banner', {
        backgroundPositionY: '35%',
        ease: 'none',
        scrollTrigger: {
          trigger: '.blog-page-banner',
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isInitialized, resblogs, langKey]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderBlogs = () => {
    const blogs = resblogs?.data?.blogs || resblogs?.data?.latestBlogs || (Array.isArray(resblogs?.data) ? resblogs.data : []);
    
    if (blogsLoading) return (
      <div className="text-center py-40 w-100">
        <p className="premium-loader-text">{t('loading')}</p>
      </div>
    );

    if (blogs.length === 0) return (
      <div className="text-center py-40 w-100">
        <p className="text-muted opacity-50">{t('noBlogs')}</p>
      </div>
    );

    return blogs.map((blog, idx) => {
      const title = blog?.title?.[langKey] || blog?.title?.en || '';
      const excerpt = blog?.short_description?.[langKey] || blog?.short_description?.en || '';
      const blogImage = getImageUrl(blog?.profileImg || blog?.image) || 
                       'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=800&q=80';
      const date = blog?.createdAt ? new Date(blog.createdAt).toLocaleDateString(isAR ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
      const category = blog?.category?.[langKey] || 'Insight';

      return (
        <Col lg={4} md={6} key={blog._id} className="editorial-card-wrap mb-10">
          <Link to={`/BlogDetails/${blog._id}/${blog?.slug?.[langKey] || blog?.slug?.en}`} className="editorial-tile">
            <div className="tile-img-container">
              <img 
                src={blogImage} 
                alt={title} 
                className="tile-img" 
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = '/Flyvia Logo.png';
                }}
              />
              <div className="tile-category-tag">{category}</div>
            </div>
            <div className="tile-body">
              <div className="tile-meta">
                <span className="tile-date">{date}</span>
                <span className="tile-dot">•</span>
                <span className="tile-author">FlyVia Editorial</span>
              </div>
              <h3 className="tile-title">{title}</h3>
              <p className="tile-excerpt">{truncateText(excerpt)}</p>
              <div className="tile-footer">
                <span className="tile-btn">
                  {t('readMore')} 
                  <FaArrowRight className="tile-arrow" />
                </span>
              </div>
            </div>
          </Link>
        </Col>
      );
    });
  };

  if (!isInitialized) return null;

  return (
    <div className="blog-editorial-page" ref={sectionRef} dir={isAR ? 'rtl' : 'ltr'}>
      <SEO title={t('pageTitle')} description={t('pageSubtitle')} />
      
      <style>{`
        .blog-editorial-page { font-family: var(--font-primary); background: #fcfcfd; color: #07111f; }
        
        .blog-page-banner {
          position: relative;
          height: 55vh; min-height: 600px;
          background: linear-gradient(rgba(7, 17, 31, 0.75), rgba(7, 17, 31, 0.85)), 
                      url('https://images.unsplash.com/photo-1499750310107-5fef28a66643?q=80&w=2070&auto=format&fit=crop');
          background-size: cover; background-position: center;
          display: flex; align-items: center; justify-content: center; text-align: center; color: #fff;
          clip-path: polygon(0 0, 100% 0, 100% 88%, 50% 100%, 0 88%);
        }

        .blog-banner-reveal h1 {
          font-size: clamp(25px, 7vw, 55px); font-weight: 900; margin-bottom: 20px;
          text-transform: uppercase; letter-spacing: -3px; line-height: 0.95;
        }

        .blog-banner-reveal p {
          max-width: 680px; margin: 0 auto 30px; font-size: 20px; opacity: 0.85; line-height: 1.6;
        }

        .blog-breadcrumbs {
          display: inline-flex; align-items: center; gap: 12px;
          background: rgba(255,255,255,0.1); padding: 10px 25px; border-radius: 50px;
          backdrop-filter: blur(10px); font-weight: 800; font-size: 14px;
        }
        .blog-breadcrumbs a { color: #fff; text-decoration: none; opacity: 0.7; transition: 0.3s; }
        .blog-breadcrumbs a:hover { opacity: 1; color: var(--primary-color); }
        .blog-breadcrumbs span { color: var(--primary-color); }

        /* Editorial Grid */
        .blog-editorial-grid { padding: 20px 0; position: relative; }
        
        .editorial-tile {
          display: block; background: #fff; border-radius: 35px; overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.03); transition: 0.5s var(--ease);
          text-decoration: none; color: inherit; border: 1px solid #f1f5f9; height: 100%;
        }
        .editorial-tile:hover {
          transform: translateY(-12px); box-shadow: 0 35px 80px rgba(174,0,1,0.08); border-color: #ae000133;
        }

        .tile-img-container { position: relative; height: 260px; overflow: hidden; }
        .tile-img { width: 100%; height: 100%; object-fit: cover; transition: 1s var(--ease); }
        .editorial-tile:hover .tile-img { transform: scale(1.1); }
        
        .tile-category-tag {
          position: absolute; top: 25px; ${isAR ? 'right' : 'left'}: 25px;
          background: #ae0001; color: #fff; padding: 6px 18px; border-radius: 50px;
          font-weight: 900; font-size: 11px; text-transform: uppercase; letter-spacing: 1px;
        }

        .tile-body { padding: 40px; }
        .tile-meta { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; font-weight: 800; font-size: 12px; color: #94a3b8; text-transform: uppercase; }
        .tile-dot { color: #ae0001; font-size: 18px; }
        
        .tile-title {
          font-family: var(--font-heading); font-size: 26px; font-weight: 900; margin-bottom: 15px;
          line-height: 1.2; letter-spacing: -0.5px; transition: 0.3s;
        }
        .editorial-tile:hover .tile-title { color: #ae0001; }

        .tile-excerpt { font-size: 15px; color: #64748b; line-height: 1.7; margin-bottom: 25px; }

        .tile-footer { border-top: 1px solid #f1f5f9; padding-top: 25px; }
        .tile-btn { 
          display: inline-flex; align-items: center; gap: 10px; font-weight: 900; font-size: 14px; 
          color: #07111f; text-transform: uppercase; letter-spacing: 1px; transition: 0.3s;
        }
        .tile-arrow { transition: 0.3s; font-size: 12px; transform: ${isAR ? 'rotate(180deg)' : 'none'}; }
        .editorial-tile:hover .tile-btn { color: #ae0001; }
        .editorial-tile:hover .tile-arrow { transform: ${isAR ? 'translateX(-8px) rotate(180deg)' : 'translateX(8px)'}; }

        /* Pagination Premium */
        .premium-pagination { display: flex; justify-content: center; gap: 10px; margin-top: 60px; }
        .page-node {
          width: 50px; height: 50px; border-radius: 15px; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; font-weight: 900;
          cursor: pointer; transition: 0.3s; color: #64748b; background: #fff;
        }
        .page-node:hover { border-color: #ae0001; color: #ae0001; transform: translateY(-3px); }
        .page-node.active { background: #ae0001; border-color: #ae0001; color: #fff; box-shadow: 0 10px 20px #ae000133; }
        .page-node.disabled { opacity: 0.4; cursor: not-allowed; }

        .premium-loader-text { font-weight: 900; font-size: 14px; color: #ae0001; text-transform: uppercase; letter-spacing: 3px; }

        @media (max-width: 992px) {
          .blog-page-banner { height: 45vh; clip-path: polygon(0 0, 100% 0, 100% 92%, 50% 100%, 0 92%); }
          .tile-body { padding: 30px; }
        }
      `}</style>

      {/* Banner */}
      <section className="blog-page-banner">
        <Container className="blog-banner-reveal">
          <h1>{t('pageTitle')}</h1>
          <p>{t('pageSubtitle')}</p>
          <div className="blog-breadcrumbs">
            <Link to="/">{t('breadcrumbHome')}</Link>
            {isAR ? <FaArrowLeft /> : <FaArrowRight />}
            <span>{t('breadcrumbCurrent')}</span>
          </div>
        </Container>
      </section>

      {/* Grid */}
      <section className="blog-editorial-grid">
        <Container>
          <Row>
            {renderBlogs()}
          </Row>

          {totalPages > 1 && (
            <div className="premium-pagination">
              <button 
                className={`page-node ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              >
                <FaArrowLeft style={{ transform: isAR ? 'rotate(180deg)' : 'none' }} />
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i + 1} 
                  className={`page-node ${i + 1 === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button 
                className={`page-node ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              >
                <FaArrowRight style={{ transform: isAR ? 'rotate(180deg)' : 'none' }} />
              </button>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};
